import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import arrayMove from 'array-move';
import Sortable from '../../Sortable';
import ImageHandler from './ImageHandler';
import AudioHandler from './AudioHandler';
import Input from '../../Input';
import produce from 'immer';
import { ReactComponent as Delete } from '../../../assets/svg/delete.svg';
import { ReactComponent as Edit } from '../../../assets/svg/edit.svg';
import { ReactComponent as Add } from '../../../assets/svg/add.svg';
import { useImages } from '../../../hooks/useImages';
import { useAudios } from '../../../hooks/useAudios';
import AudioPlay from '../../AudioPlay';
import Modal from '../../Modal';
import Button from '../../Button';
import clean from 'clean-deep';
import SimpleBar from 'simplebar-react';
import 'simplebar/dist/simplebar.min.css';

const LessonEditDataSelect = ({ data = [], onChange = () => {} }) => {
  const [state, setState] = useState([]);

  const [extractedAudios, setExtractedAudios] = useState([]);
  const [extractedImages, setExtractedImages] = useState([]);

  const images = useImages(extractedImages);
  const audios = useAudios(extractedAudios);

  const [showItemModal, setShowItemModal] = useState(undefined);
  const [itemText, setItemText] = useState('');
  const [itemImage, setItemImage] = useState(undefined);
  const [itemAudio, setItemAudio] = useState(undefined);
  const [itemAlternatives, setItemAlternatives] = useState([]);

  const [showAlternativeModal, setShowAlternativeModal] = useState(undefined);
  const [alternativeText, setAlternativeText] = useState('');
  const [alternativeAudio, setAlternativeAudio] = useState(undefined);

  useEffect(() => {
    const state = formatState(data);
    setState(state);
    setExtractedImages(state.map((i) => i.image));
    setExtractedAudios(() => {
      const audios = [];
      state.forEach((item) => {
        audios.push(item.audio);
        item.alternatives.forEach((alternative) => {
          audios.push(alternative.audio);
        });
      });
      return audios;
    });
  }, [data]);

  useEffect(() => {
    onChange({
      data: clean(state),
    });
  }, [state, onChange]);

  const isLoaded = ({ image, audio }) =>
    ((image && images[image]) || !image) && ((audio && audios[audio]) || !audio);

  const onSortEnd = ({ oldIndex, newIndex }) => setState((state) => arrayMove(state, oldIndex, newIndex));

  const onDeleteItem = (index) =>
    setState(
      produce((state) => {
        state.splice(index, 1);
      })
    );

  const onAddItem = () => {
    setItemAudio(undefined);
    setItemImage(undefined);
    setItemAlternatives([]);
    setItemText('');
    setShowItemModal({
      submit: 'Agregar',
      onSubmit: ({ text, audio, image, alternatives }) => {
        setExtractedImages((state) => [...state, image]);
        setExtractedAudios((state) => [...state, audio]);
        setState(
          produce((state) => {
            state.push({
              text: text,
              audio,
              image,
              alternatives,
            });
          })
        );
        setShowItemModal(undefined);
      },
    });
  };

  const onEditItem = (index) => {
    setItemAudio(state[index].audio);
    setItemImage(state[index].image);
    setItemText(state[index].text);
    setItemAlternatives(state[index].alternatives);
    setShowItemModal({
      submit: 'Guardar',
      onSubmit: ({ text, audio, image, alternatives }) => {
        setExtractedImages((state) => [...state, image]);
        setExtractedAudios((state) => [...state, audio]);
        setState(
          produce((state) => {
            state[index].text = text;
            state[index].audio = audio;
            state[index].image = image;
            state[index].alternatives = alternatives;
          })
        );
        setShowItemModal(undefined);
      },
    });
  };

  const onSortEnd_Alternatives = ({ oldIndex, newIndex }) =>
    setItemAlternatives((state) => {
      const moved = arrayMove(state, oldIndex, newIndex);
      return produce(moved, (state) => {
        state.forEach((item, index, array) => {
          if (index === 0) array[index].selected = true;
          else array[index].selected = false;
        });
      });
    });

  const onDeleteAlternative = (index) =>
    setItemAlternatives(
      produce((state) => {
        state.splice(index, 1);
        state.forEach((item, index, array) => {
          if (index === 0) array[index].selected = true;
          else array[index].selected = false;
        });
      })
    );

  const onAddAlternative = () => {
    setAlternativeAudio(undefined);
    setAlternativeText('');
    setShowAlternativeModal({
      submit: 'Agregar',
      onSubmit: ({ text, audio }) => {
        setExtractedAudios((state) => [...state, audio]);
        setItemAlternatives(
          produce((state) => {
            state.push({
              text: text,
              audio,
              selected: state.length === 0,
            });
          })
        );
        setShowAlternativeModal(undefined);
      },
    });
  };

  const onEditAlternative = (index) => {
    setAlternativeAudio(itemAlternatives[index].audio);
    setAlternativeText(itemAlternatives[index].text);
    setShowAlternativeModal({
      submit: 'Guardar',
      onSubmit: ({ text, audio }) => {
        setExtractedAudios((state) => [...state, audio]);
        setItemAlternatives(
          produce((state) => {
            state[index].text = text;
            state[index].audio = audio;
          })
        );
        setShowAlternativeModal(undefined);
      },
    });
  };

  return (
    <Container>
      {showAlternativeModal && (
        <ModalStyled>
          <ModalContainer>
            <ModalBody>
              <Handlers>
                <AudioHandler audio={alternativeAudio} onChange={(obj) => setAlternativeAudio(obj.name)} />
              </Handlers>
              <Input
                placeholder='Texto'
                value={alternativeText}
                onChange={(e) => setAlternativeText(e.target.value)}
              />
            </ModalBody>
            <ModalButtons>
              <Button secondary text='Cancelar' onClick={() => setShowAlternativeModal(undefined)} />
              <Button
                primary
                text={showAlternativeModal.submit}
                onClick={() =>
                  showAlternativeModal.onSubmit({
                    text: alternativeText,
                    audio: alternativeAudio,
                  })
                }
              />
            </ModalButtons>
          </ModalContainer>
        </ModalStyled>
      )}
      {showItemModal && (
        <ModalStyled>
          <ModalContainer>
            <SimpleBar style={{ maxHeight: '60vh' }}>
              <ModalBody>
                <Handlers>
                  <ImageHandler image={itemImage} onChange={(obj) => setItemImage(obj.name)} />
                  <AudioHandler audio={itemAudio} onChange={(obj) => setItemAudio(obj.name)} />
                </Handlers>
                <Input placeholder='Texto' value={itemText} onChange={(e) => setItemText(e.target.value)} />
                <Sortable
                  items={itemAlternatives}
                  render={(item, index) => (
                    <Wrapper>
                      {isLoaded(item) ? (
                        <>
                          <Detail>
                            {item.audio && audios[item.audio] && <AudioPlay audio={audios[item.audio]} />}
                            {item.text && <Text>{item.text}</Text>}
                          </Detail>
                          <Options>
                            <OptionIcon
                              as={Delete}
                              color='#c62828'
                              onClick={() => onDeleteAlternative(index)}
                            />
                            <OptionIcon as={Edit} color='#1976d2' onClick={() => onEditAlternative(index)} />
                          </Options>
                        </>
                      ) : (
                        <Spinner src='loading.gif' />
                      )}
                    </Wrapper>
                  )}
                  onSortEnd={onSortEnd_Alternatives}
                />
                <WrapperAdd onClick={onAddAlternative}>
                  <OptionIcon as={Add} color='#2196f3' />
                </WrapperAdd>
              </ModalBody>
            </SimpleBar>
            <ModalButtons>
              <Button secondary text='Cancelar' onClick={() => setShowItemModal(undefined)} />
              <Button
                primary
                text={showItemModal.submit}
                onClick={() =>
                  showItemModal.onSubmit({
                    text: itemText,
                    image: itemImage,
                    audio: itemAudio,
                    alternatives: itemAlternatives,
                  })
                }
              />
            </ModalButtons>
          </ModalContainer>
        </ModalStyled>
      )}
      <Sortable
        items={state}
        render={(item, index) => (
          <Wrapper>
            {isLoaded(item) ? (
              <>
                <Detail>
                  {item.image && images[item.image] && <ImageTiny src={images[item.image]} />}
                  {item.audio && audios[item.audio] && <AudioPlay audio={audios[item.audio]} />}
                  {item.text && <Text>{item.text}</Text>}
                </Detail>
                <Options>
                  <OptionIcon as={Delete} color='#c62828' onClick={() => onDeleteItem(index)} />
                  <OptionIcon as={Edit} color='#1976d2' onClick={() => onEditItem(index)} />
                </Options>
              </>
            ) : (
              <Spinner src='loading.gif' />
            )}
          </Wrapper>
        )}
        onSortEnd={onSortEnd}
      />
      <WrapperAdd onClick={onAddItem}>
        <OptionIcon as={Add} color='#2196f3' />
      </WrapperAdd>
    </Container>
  );
};

const formatState = (state) => state.map((state) => ({ alternatives: [], ...state }));

const Container = styled.div`
  display: flex;
  flex-direction: column;
`;

const Wrapper = styled.div`
  padding: 15px 15px;
  border-radius: 10px;
  overflow: hidden;
  box-sizing: border-box;
  width: 100%;
  user-select: none;
  display: flex;
  flex-wrap: nowrap;
  align-items: center;
  justify-content: space-between;
  background-color: #caedff;
  margin-bottom: 10px;
`;

const WrapperAdd = styled.div`
  border-radius: 10px;
  border: 2px dashed #2196f3;
  min-height: 50px;
  width: 100%;
  user-select: none;
  display: flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  cursor: pointer;
`;

const Detail = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;

  & > * {
    margin: 5px;
  }
`;

const Handlers = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-around;
  & > * {
    margin: 5px;
  }
`;

const ImageTiny = styled.img`
  width: 50px;
  height: 50px;
  min-width: 50px;
  min-height: 50px;
  border-radius: 10px;
  background-color: black;
  object-fit: contain;
`;

const Text = styled.div`
  font-size: 0.9em;
`;

const Options = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
`;

const OptionIcon = styled.div`
  padding: 5px;
  width: 20px;
  height: 20px;
  min-width: 20px;
  min-height: 20px;
  cursor: pointer;
  fill: ${(props) => props.color || 'black'};
  :not(:last-child) {
    margin-right: 10px;
  }
`;

const Spinner = styled.img`
  margin: auto;
  width: 25px;
  height: 25px;
  display: block;
`;

const ModalStyled = styled(Modal)`
  max-width: 400px;
  width: 100%;
`;

const ModalContainer = styled.div`
  display: flex;
  flex-direction: column;
  padding: 20px;

  & > *:not(:last-child) {
    margin-bottom: 10px;
  }
`;

const ModalBody = styled.div`
  & > *:not(:last-child) {
    margin-bottom: 10px;
  }
`;

const ModalButtons = styled.div`
  display: flex;
  flex-wrap: nowrap;
  align-items: center;
  justify-content: space-between;
  width: 100%;

  & > * {
    margin: 5px;
  }

  @media (max-width: 240px) {
    flex-wrap: wrap;
    flex-direction: column-reverse;
    justify-content: center;
  }
`;

export default LessonEditDataSelect;
